import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { BrushCursor, CURSOR_SWITCH_THRESHOLD } from '../../../../icons';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { DEFAULT_BRUSH_SIZE } from '../../../constants';
import useCanvas from '../../../providers/hooks/use-canvas';
import useStage from '../../../providers/hooks/use-stage';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import type { BrushAnnotationT } from '../../../types';
import { applyColorToImageData, clearCanvas, drawBitmap, drawLine, drawLines, fillDrawing } from '../canvas/utils';
const Cursor = styled<'div', {
  $size: number;
}>('div', ({
  $size
}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  marginTop: `-${$size / 2}px`,
  marginLeft: `-${$size / 2}px`,
  cursor: 'none'
}));
type PropsT = {
  isDrawing?: boolean;
  annotation?: BrushAnnotationT | null;
  annotationIdx?: string;
  annotationBitmap?: ImageData;
  onPaintComplete: (() => void) | (() => Promise<void>);
  onPaintStart?: () => void;
};
const SegmentationPolygon = ({
  isDrawing,
  annotation,
  annotationBitmap,
  onPaintComplete,
  onPaintStart
}: PropsT) => {
  const cursorRef = useRef<HTMLDivElement | undefined | null>(null);
  const {
    stage,
    getStageSettings
  } = useStage();
  const stageSettings = getStageSettings();
  const [isPainting, setIsPainting] = useState(false);
  const {
    brushToolSettingsAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const {
    eraserSelected,
    size
  } = useAtomValue(brushToolSettingsAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const [segmentationPoints, setSegmentationPoints] = useState<{
    point: [number, number];
    size: number;
  }[]>([]);
  useEffect(() => {
    if (annotation) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSegmentationPoints([]);
    }
  }, [annotation]);
  const {
    getDrawingCanvasContext
  } = useCanvas();
  const drawingCtx = getDrawingCanvasContext();
  const scaledSize = size ? size / stageSettings.scale : DEFAULT_BRUSH_SIZE;

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const drawExistingBrushData = useCallback(async () => {
    if (!annotationLabel || !drawingCtx) return;
    clearCanvas(drawingCtx);
    if (annotation) {
      const existingBitmap = annotationBitmap;
      if (existingBitmap) {
        applyColorToImageData(existingBitmap, annotationLabel.style.strokeColor);
        drawBitmap(drawingCtx, existingBitmap);
      }
    }
    if (segmentationPoints) {
      drawLines({
        ctx: drawingCtx,
        points: segmentationPoints,
        color: annotationLabel.style.strokeColor,
        eraserSelected
      });
    }
  }, [annotation, annotationLabel, drawingCtx, eraserSelected, segmentationPoints]);
  const drawSegmentationPoints = useCallback(() => {
    if (!annotationLabel || !drawingCtx || !segmentationPoints) return;
    clearCanvas(drawingCtx);
    drawLines({
      ctx: drawingCtx,
      points: segmentationPoints,
      color: annotationLabel.style.strokeColor,
      eraserSelected
    });
  }, [annotationLabel, drawingCtx, eraserSelected, segmentationPoints]);
  const addPoint = useCallback((mousePos: [number, number]) => {
    const newPoints = [...segmentationPoints];
    newPoints.push({
      point: mousePos,
      size: scaledSize
    });
    setSegmentationPoints(newPoints);
  }, [segmentationPoints, scaledSize]);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;

    // start annotation
    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel || !drawingCtx) return;
    addPoint(mousePos);

    // draw existing points and start drawing by setting isPainting to true
    if (!isPainting) {
      drawLine({
        ctx: drawingCtx,
        point: mousePos,
        size: scaledSize,
        color: annotationLabel.style.strokeColor,
        eraserSelected,
        moveFirstPoint: mousePos
      });
      setIsPainting(true);
      onPaintStart?.();
    }
  }, [annotationLabel, drawingCtx, addPoint, isPainting, scaledSize, eraserSelected, onPaintStart]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    // update top and left css of cursor ref to mouse Pos
    if (cursorRef.current) {
      cursorRef.current.style.top = `${stageEvent.detail.mousePos[1]}px`;
      cursorRef.current.style.left = `${stageEvent.detail.mousePos[0]}px`;
      cursorRef.current.style.marginTop = `-${scaledSize / 2}px`;
      cursorRef.current.style.marginLeft = `-${scaledSize / 2}px`;
    }
    if (!annotationLabel || !drawingCtx || !isPainting) return;
    const {
      mousePos,
      shiftKey,
      mouseLeftDown
    } = stageEvent.detail;
    if (shiftKey || mouseLeftDown) {
      addPoint(mousePos);
    } else {
      drawSegmentationPoints();
    }
    if (segmentationPoints.length > 0) drawLine({
      ctx: drawingCtx,
      point: mousePos,
      size: scaledSize,
      color: annotationLabel.style.strokeColor,
      eraserSelected,
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      moveFirstPoint: segmentationPoints[segmentationPoints.length - 1].point
    });
  }, [annotationLabel, drawingCtx, isPainting, segmentationPoints, scaledSize, eraserSelected, addPoint, drawSegmentationPoints]);
  const onPointerUp = useCallback((stageEvent: StagePointerEventT) => {
    drawExistingBrushData();
  }, [drawExistingBrushData]);
  const onDoubleClick = useCallback(async (stageEvent: StagePointerEventT) => {
    setIsPainting(false);
    const drawingCtx = getDrawingCanvasContext();
    if (!annotationLabel || !drawingCtx) return;
    await drawExistingBrushData();
    fillDrawing(drawingCtx, annotationLabel.style.strokeColor, eraserSelected);
    drawingCtx.closePath();
    onPaintComplete();
  }, [getDrawingCanvasContext, annotationLabel, drawExistingBrushData, eraserSelected, onPaintComplete]);
  useEffect(() => {
    if (!stage) return;
    if (isDrawing) {
      stage.addEventListener('pointerdown', onPointerDown);
      stage.addEventListener('pointermove', onPointerMove);
      stage.addEventListener('pointerup', onPointerUp);
      stage.addEventListener('doubleclick', onDoubleClick);
      return () => {
        stage.removeEventListener('pointerdown', onPointerDown);
        stage.removeEventListener('pointerup', onPointerUp);
        stage.removeEventListener('pointermove', onPointerMove);
        stage.removeEventListener('doubleclick', onDoubleClick);
      };
    }
  }, [isDrawing, stage, onPointerDown, onPointerMove, onPointerUp, onDoubleClick]);
  return <>
      {scaledSize > CURSOR_SWITCH_THRESHOLD &&
    // @ts-expect-error missing null check
    <Cursor data-testid="cursor" ref={cursorRef} $size={scaledSize}>
          <BrushCursor size={scaledSize} />
        </Cursor>}
    </>;
};
export default SegmentationPolygon;